import React, { FC, useEffect, useState, useMemo, useContext } from 'react';
import useTranslations from '@hooks/useTranslations';
import Collapsible from 'react-collapsible';
import Icon, { Icons } from '@components/Icon';
import {
    FINANCE_DETAIL_VIEW,
    isBrandAC,
    isBrandAP,
    isBrandDS,
    isBrandOV,
    isMarketGB,
    isMarketIT,
    LEASYS_PROVIDER_TYPE,
    ModalVersion,
    PxVersion,
    ROLES,
} from 'src/constants/main';
import IconButton from '@components/IconButton';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';
import { useMoneyFormatter } from '@hooks/useMoneyFormatter';
import { useJourneyType } from '@hooks/useJourneyType';
import {
    getFinanceDetailText,
    getFinanceDetailView,
    getMonthlyPriceObjByType,
    getStorePriceByPaymentMethod,
    renderPromotionalText,
} from '@utils/Price.utils';
import { usePrices } from '@hooks/usePriceCalculator';
import { StockFormatedPriceType } from 'src/services/stock/models/stockItem.price.model';
import PriceSuffix from '@components/PriceSuffix';
import { ICalculateSummaryRequestData } from 'src/services/calculateSummary/calculateSummary.types';
import { useFinanceWidgetDuck } from '@hooks/useFinanceWidgetDuck';
import { useSummaryDataPromise } from '@components/OfferListItem/hooks/useSummaryDataPromise';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { buildParameterizedPathname } from '@utils/url.utils';
import { IConfiguratorOption } from 'src/redux/configurator/configurator.duck.interface';
import { CarJourneyType } from 'src/services';
import { useCarStockJourneyCheck } from 'src/hooks/useCarStockJourneyCheck';
import { FEATURES_LIST, FeatureSwitchContext, IFinanceWidgetButtonType } from 'src/context/featureSwitchApp';
import routes from 'src/constants/routes';
import { useSelector } from 'react-redux';
import { getFormattedOfferValidityDate } from '@utils/Car.utils';
import { SummaryPriceDetailsProps } from '@components/SummaryPriceDetails/index';
import { theme } from 'src/styles/theme';
import Spinner from '@components/Spinner';
import { useOrdelPanelLogic } from '@hooks/useOrdelPanelLogic';
import { AccessibleButton } from '@components/Button/AccessibleButton';
import { PaymentJourneyTypes } from 'src/partExchange/interfaces/Default';
import { getAgentDiscount, getPromoCodeDiscount } from '@utils/Deal.utils';
import MonthlyPriceLabel, { AprSizes } from '@components/MonthlyPriceLabel';
import parser from 'bbcode-to-react';
import { useIsShowPriceExcludedVAT } from '@hooks/useIsShowPriceExcludedVAT';
import { PriceVatExcluded } from '@components/PriceVatExcluded';
import UIDuck from 'src/redux/commonDucks/ui.duck';
import VehiclePriceDetailsBlock from '@components/v2/VehiclePriceDetailsBlock';
import { IconTemplate } from '@components/Icon/IconTemplate';
import Button from '@components/Button';
import { useRouter } from 'next/router';

import ConfiguratorDuck from 'src/redux/configurator/configurator.duck';
import { Divider } from '../../pages/summary';
import { useDispatch } from 'react-redux';
import { PERSONALIZE_OFFER_SECTIONS } from '@components/Modal/ModalPersonalizeOffer/ModalPersonalizeOfferTemplate';
import { useUserDuck } from '@hooks/useUserDuck';
import { useGTM } from '@hooks/useGTM';
import FinanceWidgetDuck from '../../redux/financeWidget/financeWidget.duck';
import { Redux } from '../../redux/redux.interface';
import { getFeatureSwitch } from '@utils/featureSwitches.utils';
import { removeSimpleValuation } from '../../partExchange/store/actions';

const SummaryPriceDetailsTemplate: FC<SummaryPriceDetailsProps> = ({
    className,
    handleFinanceWidgetBtnClick,
    isMobile,
    isBasket,
    isDealer,
    isOpenPriceDetails,
    setIsOpenPriceDetails,
}) => {
    const { t } = useTranslations();
    const { carDetails, currentDeal } = useCarDetailsDuck();
    const context = useContext(FeatureSwitchContext);
    const { formatMoney } = useMoneyFormatter();
    const router = useRouter();
    const dispatch = useDispatch();
    const { pushToDataLayer } = useGTM();
    const { financeQuote, globalTexts, pricesList } = useFinanceWidgetDuck();
    const { isUserLoggedInAsDealer } = useUserDuck();
    const { paymentJourneyType, isRent } = useJourneyType();
    let { totalCashPrice } = useOrdelPanelLogic();
    const { isStockJourney } = useCarStockJourneyCheck();
    const { cash, finance } = usePrices(carDetails ?? null);
    const { totalBasicPriceWithoutVat } = cash;
    const {
        totalPrice: totalMonthlyPrice,
        financeProperties: { hasFinanceVariants, deposit, apr },
    } = finance;
    const isLoading = useSelector((state) => UIDuck.isLoading(state));
    const leasysQuote = useSelector(FinanceWidgetDuck.getLeasysQuote);
    const autobizPxState = useSelector((state: Redux) => ConfiguratorDuck.getOwnState(state).pxState);

    const pxVersion = getFeatureSwitch(FEATURES_LIST.FEATURE_SWITCH_PX_VERSION, context);

    const [isOpen, setIsOpen] = useState(isOpenPriceDetails);

    useEffect(() => {
        setIsOpen(isOpenPriceDetails);
    }, [isOpenPriceDetails]);

    const setOpen = setIsOpenPriceDetails ?? setIsOpen;
    const newPathParams = {
        nameplateBodyStyleSlug: carDetails.nameplateBodyStyleSlug,
        speckPackSlug: carDetails.specPackSlug,
        engineGearboxFuelSlug: carDetails.engineGearboxFuelSlug,
        exteriorColourSlug: carDetails.exteriorColourSlug,
        interiorColourSlug: carDetails.interiorColourSlug,
        options: (carDetails.options?.map((option: IConfiguratorOption) => option.id) || []).join(','),
        financeQuoteId: currentDeal?.financeQuoteId,
    };
    const modelURL = buildParameterizedPathname(routes.CAR, CarJourneyType.CONFIGURABLE, newPathParams);
    const open = useMemo(() => isOpenPriceDetails ?? isOpen, [isOpenPriceDetails, isOpen]);

    const params: ICalculateSummaryRequestData = {
        carConfigId: carDetails._id ?? carDetails.externalId,
        token: financeQuote?.token ?? currentDeal?.financeQuoteId,
        carConfiguration: carDetails,
    };

    const { processCalculateSummaryDataPromise } = useSummaryDataPromise({
        ...params,
        ...(isRent && currentDeal?.extraFields?.quotationId && { quotationId: currentDeal?.extraFields?.quotationId }),
    });

    const isValidityDateEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_OFFER_VALIDITY_DATE_ENABLED);
    const isFinanceDisabled = !useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_IS_FINANCE_DISABLED);

    const showOfferValidityDate: boolean = isValidityDateEnabled && isFinanceDisabled && hasFinanceVariants;
    const isFinanceOverlayForcedToLink: boolean = useFeatureSwitchEnabled(
        FEATURES_LIST.FEATURE_SWITCH_FORCE_FINANCE_WIDGET_BUTTON,
        IFinanceWidgetButtonType.LINK
    );
    const isFinanceOverlayForcedToIcon: boolean = useFeatureSwitchEnabled(
        FEATURES_LIST.FEATURE_SWITCH_FORCE_FINANCE_WIDGET_BUTTON,
        IFinanceWidgetButtonType.ICON
    );
    const isFinanceWidgetDisabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_DISABLED_FINANCE_WIDGET);
    const availableOptions = useSelector((state) => ConfiguratorDuck.getOwnState(state).availableOptions);

    const stockModelUrl = buildParameterizedPathname(routes.SELECTOR, CarJourneyType.STOCK, {
        nameplateBodyStyleSlug: carDetails.nameplateBodyStyleSlug,
        modelId: carDetails.model.id,
        bodyStyleId: carDetails.bodyStyle.id,
    });

    const addAvailableOptions = (modelUrl: string) => {
        if (availableOptions?.length > 0) {
            return `${modelUrl}${modelUrl.includes('?') ? '&' : '?'}optionsavailable=${availableOptions.join(',')}`;
        }

        return modelUrl;
    };

    const addFinanceQuoteIdToUrl = (modelUrl: string) => {
        if (currentDeal?.financeQuoteId && !isRent) {
            return `${modelUrl}${modelUrl.includes('?') ? '&' : '?'}financeQuoteId=${currentDeal.financeQuoteId}`;
        }

        return modelUrl;
    };

    const handleCustomizeBtnClick = () => {
        const onContinue = () => {
            if (currentDeal?.extraFields?.customBackUrl) {
                window.location.href = currentDeal?.extraFields?.customBackUrl;
                return;
            }

            const mtoRedirectUrl = `${modelURL}${modelURL.includes('?') ? '&' : '?'}reSaveOptionsOnVehicle=true`;

            return router.push(carDetails.stock ? stockModelUrl : addFinanceQuoteIdToUrl(mtoRedirectUrl));
        };

        if (pxVersion === PxVersion.v2 && autobizPxState) {
            dispatch(
                UIDuck.openModal({
                    data: {
                        onContinueClick: () => onContinue(),
                        description: t('modal.incompletePx.modify.description'),
                        title: t('modal.incompletePx.modify.title'),
                        backButton: t('modal.incompletePx.modify.button.close'),
                        continueButton: t('modal.incompletePx.modify.button.continue'),
                    },
                    modalType: UIDuck.MODAL_TYPES.INCOMPLETE_PX,
                    modalVersion: ModalVersion.v2,
                    modalProperties: {
                        centerModeOnDesktop: true,
                    },
                })
            );

            return;
        }

        onContinue();
    };

    const isSOLEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_SOL_ENABLED);

    const amountRounding = getMonthlyPriceObjByType(carDetails.prices, StockFormatedPriceType.EMPLOYEE)?.amountRounding;

    const financeDetailView = getFinanceDetailView(carDetails.prices, StockFormatedPriceType.EMPLOYEE);
    const financeDetailText = getFinanceDetailText(
        carDetails.prices,
        StockFormatedPriceType.EMPLOYEE,
        globalTexts,
        pricesList,
        carDetails.externalId || carDetails.vehicleId
    );
    const promoText =
        financeQuote?.primaryText ?? (carDetails?.extraFields?.promotionalText || carDetails?.promotionalText);

    const showFOLink =
        (financeDetailView === FINANCE_DETAIL_VIEW.LINK || isFinanceOverlayForcedToLink) &&
        !isFinanceOverlayForcedToIcon;

    const offerValidityDate = getFormattedOfferValidityDate(carDetails?.importInputs);

    const { hasSelectedPaymentJourney = false } = currentDeal?.extraFields ?? {};

    const isShowPriceExcludedVAT: boolean = useIsShowPriceExcludedVAT();

    const showCashAndFinancePrice = !hasSelectedPaymentJourney;
    const showOnlyCashPrice = hasSelectedPaymentJourney && paymentJourneyType === PaymentJourneyTypes.CASH;
    const showOnlyFinancePrice = hasSelectedPaymentJourney && paymentJourneyType === PaymentJourneyTypes.FINANCE;

    totalCashPrice = isBasket
        ? getStorePriceByPaymentMethod(
              currentDeal,
              hasSelectedPaymentJourney ? paymentJourneyType : null,
              isSOLEnabled
          ) +
          getPromoCodeDiscount(currentDeal, hasSelectedPaymentJourney ? paymentJourneyType : null) +
          getAgentDiscount(currentDeal)
        : totalCashPrice +
          getPromoCodeDiscount(currentDeal, hasSelectedPaymentJourney ? paymentJourneyType : null) +
          getAgentDiscount(currentDeal);

    const noValidityDate = !(showOfferValidityDate && offerValidityDate);
    const isFinanceAvailable =
        !noValidityDate ||
        (totalMonthlyPrice && !isFinanceWidgetDisabled && showOnlyFinancePrice) ||
        (showOnlyFinancePrice && financeDetailView === FINANCE_DETAIL_VIEW.LINK);

    return (
        <div id="price" className={open ? `${className} isOpen` : `${className} isClosed`}>
            {!isBasket && !isDealer && <Divider />}
            <div id={!isMobile ? 'skip-to-content' : ''}>
                <Collapsible
                    lazyRender={false}
                    open={open}
                    trigger={
                        <AccessibleButton
                            id={!isMobile ? 'price-section' : ''}
                            tabIndex={0}
                            onClick={() => setOpen(!open)}
                            className="titleWrapperButton"
                        >
                            <div className="titleWrapper">
                                <span className="title" data-testid="TESTING_VEHICLE_SUMMARY_TITLE_TEXT">
                                    {t('summary.price.title')}
                                </span>
                                <Icon
                                    className={open ? 'isOpen' : 'isClose'}
                                    name={open ? Icons.ChevronUp : Icons.ChevronDown}
                                    dataTestId="TESTING_VEHICLE_SUMMARY_TITLE_ICON"
                                />
                            </div>
                        </AccessibleButton>
                    }
                >
                    <VehiclePriceDetailsBlock />
                    <div className="configuration">
                        <button onClick={handleCustomizeBtnClick}>
                            <IconTemplate name={Icons.edit} />
                            {t(`summary.configuration.button${isStockJourney ? '.stock' : ''}`)}
                        </button>
                    </div>
                    <div className="totalPriceWrapper">
                        <div className="titleValidDateWrapper">
                            <div className="totalPriceTitle">{t('summary.totalPrice')}</div>
                            {isFinanceAvailable && showOfferValidityDate && offerValidityDate && (
                                <div className="titleValidDateWrapper__validDate">
                                    <span>
                                        {t('basket.offerValidityDate')} {offerValidityDate}
                                    </span>
                                </div>
                            )}
                        </div>
                        <div
                            className={`totalPrice ${isShowPriceExcludedVAT ? 'isShowPriceExcludedVAT-container' : ''}`}
                        >
                            {isLoading ? (
                                <Spinner size={20} border={3} color={theme.colors.black} />
                            ) : (
                                <>
                                    {(showCashAndFinancePrice || showOnlyCashPrice) && (
                                        <div
                                            className="cashPrice"
                                            data-testid="TESTING_VEHICLE_SUMMARY_TOTAL_CASH_PRICE"
                                        >
                                            {formatMoney(totalCashPrice - getAgentDiscount(currentDeal), true)}
                                            <PriceSuffix showOnlyPrioritizedTranslation checkPrioritizedTranslation />
                                        </div>
                                    )}
                                    {isShowPriceExcludedVAT && (
                                        <div className="cashPrice isShowPriceExcludedVAT">
                                            <PriceVatExcluded price={formatMoney(totalBasicPriceWithoutVat, true)} />
                                        </div>
                                    )}
                                    {isRent && (
                                        <div
                                            className="monthlyPrice"
                                            data-testid="TESTING_VEHICLE_SUMMARY_TOTAL_MONTHLY_PRICE"
                                        >
                                            <div className="monthlyPriceWrapper">
                                                {formatMoney(leasysQuote?.monthlyPayment, false, '', amountRounding)}
                                                <span className="suffix">
                                                    <PriceSuffix checkPrioritizedTranslation isMonthlyPrice />
                                                </span>
                                            </div>
                                            <div className="primaryText">{leasysQuote?.primaryText?.[0]?.text}</div>
                                            <MonthlyPriceLabel
                                                withPrimaryColor={isMarketGB}
                                                alignToRight
                                                isSummary
                                                aprValue={apr}
                                                aprCustomTextKey={promoText}
                                                size={AprSizes.MEDIUM}
                                                calculateSummaryData={processCalculateSummaryDataPromise}
                                                financeDetailText={t('summary.financeDetail')}
                                            />
                                        </div>
                                    )}
                                    {!isRent &&
                                        !isShowPriceExcludedVAT &&
                                        (showCashAndFinancePrice || showOnlyFinancePrice) &&
                                        totalMonthlyPrice && (
                                            <>
                                                <div
                                                    className="monthlyPrice"
                                                    data-testid="TESTING_VEHICLE_SUMMARY_TOTAL_MONTHLY_PRICE"
                                                >
                                                    <div className="monthlyPriceWrapper">
                                                        {!showOnlyFinancePrice && (
                                                            <span className="prefix">
                                                                {t('configurator.optionPrice.suffix')}
                                                            </span>
                                                        )}
                                                        {formatMoney(totalMonthlyPrice, false, '', amountRounding)}
                                                        <span className="suffix">
                                                            <PriceSuffix checkPrioritizedTranslation isMonthlyPrice />
                                                        </span>
                                                        {isFinanceAvailable &&
                                                            financeDetailView === FINANCE_DETAIL_VIEW.ICON && (
                                                                <IconButton
                                                                    className="infoIcon"
                                                                    icon={
                                                                        isBrandAC || isBrandAP || isBrandDS
                                                                            ? Icons.InfoCircle
                                                                            : Icons.Info
                                                                    }
                                                                    width={isBrandOV ? 16 : 14}
                                                                    height={isBrandOV ? 16 : 14}
                                                                    onClick={processCalculateSummaryDataPromise}
                                                                    altId="svgImgSeeFinanceDetails-1"
                                                                    altText={financeDetailText}
                                                                    role={ROLES.IMG}
                                                                />
                                                            )}
                                                    </div>
                                                    {isFinanceAvailable &&
                                                        financeDetailView === FINANCE_DETAIL_VIEW.LINK && (
                                                            <span
                                                                className="financeDetailLinkMobile"
                                                                onClick={processCalculateSummaryDataPromise}
                                                            >
                                                                {parser.toReact(financeDetailText) ||
                                                                    t('summary.financeDetail')}
                                                            </span>
                                                        )}
                                                </div>
                                                <div className="priceInfo">
                                                    {isMarketGB && showFOLink && (
                                                        <MonthlyPriceLabel
                                                            withPrimaryColor={isMarketGB}
                                                            size={AprSizes.MEDIUM}
                                                            alignToRight
                                                            shopOnlyApr
                                                            aprValue={apr}
                                                            aprCustomTextKey={promoText}
                                                            calculateSummaryData={processCalculateSummaryDataPromise}
                                                        />
                                                    )}
                                                    {!isMarketGB &&
                                                        renderPromotionalText(
                                                            promoText,
                                                            isMarketIT && isBrandOV
                                                                ? t('price.pattern.anticipo')
                                                                : t('price.pattern'),
                                                            deposit,
                                                            null,
                                                            null,
                                                            null,
                                                            '',
                                                            { apr }
                                                        )}
                                                </div>
                                            </>
                                        )}
                                </>
                            )}
                        </div>
                    </div>
                    {!isFinanceAvailable && (
                        <div className={`finance ${noValidityDate ? 'noValidityDate' : ''}`}>
                            {showOfferValidityDate && offerValidityDate && (
                                <div className="validDate">
                                    <span>
                                        {t('basket.offerValidityDate')} {offerValidityDate}
                                    </span>
                                </div>
                            )}
                            {totalMonthlyPrice && (
                                <div className="financePersonalize">
                                    {!isFinanceWidgetDisabled && showOnlyFinancePrice && (
                                        <div className="financeWidgetLinkWrapper" onClick={handleFinanceWidgetBtnClick}>
                                            <Icon
                                                name={isBrandDS ? Icons.CalculatorDS : Icons.Calculator}
                                                width={30}
                                                height={30}
                                                className="calculatorIcon"
                                                altId="personalizeFinanceDetailsLink"
                                                altText={t('basket.paymentDetails.personalizePayment')}
                                                role={ROLES.IMG}
                                            />
                                            <button className="financeWidgetLink">
                                                {t('basket.paymentDetails.personalizePayment')}
                                            </button>
                                        </div>
                                    )}
                                    {showOnlyFinancePrice && financeDetailView === FINANCE_DETAIL_VIEW.LINK && (
                                        <div>
                                            <button
                                                className="financeDetailLinkDesktop"
                                                onClick={processCalculateSummaryDataPromise}
                                            >
                                                {parser.toReact(financeDetailText) || t('summary.financeDetail')}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                    {isUserLoggedInAsDealer && isBasket && (
                        <Button
                            onClick={() => {
                                pushToDataLayer({
                                    event: 'uaevent',
                                    eventCategory: 'Content',
                                    eventAction: 'Display::EditConfigurationModal',
                                    eventLabel: t('modal.personalizeConfiguration.edit.price.button'),
                                });
                                dispatch(
                                    UIDuck.openModal({
                                        data: {
                                            openSection: PERSONALIZE_OFFER_SECTIONS.DISCOUNT,
                                        },
                                        modalType: UIDuck.MODAL_TYPES.PERSONALIZE_OFFER,
                                        modalVersion: ModalVersion.v2,
                                        modalProperties: {
                                            withoutPadding: true,
                                        },
                                    })
                                );
                            }}
                            secondary
                            fitContentWidth
                            centerMode
                            hasEditIcon
                            withoutBackground={isBrandAC}
                            marginTop={45}
                        >
                            <IconTemplate name={Icons.edit} />
                            {t('modal.personalizeConfiguration.edit.price.button')}
                        </Button>
                    )}
                </Collapsible>
            </div>
            {!isBasket && !isDealer && <Divider isBottom isOpen={open} />}
        </div>
    );
};

export default SummaryPriceDetailsTemplate;
